import { useQueryClient } from "@tanstack/react-query";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ChangeLifecycleButtons from "eSign/components/ChangeLifecycleButtons";
import ProductsPanel from "eSign/panelsegment/ProductsPanel";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import { InvoiceV2DraftSection } from "InvoiceV2/components/DocumentDetails/InvoiceV2DraftSection";
import RecipientsPanel from "InvoiceV2/panelsegment/RecipientsPanel";
import React from "react";
import TwoColumnLayout from "../../layout/TwoColumnLayout";
import InvoiceActivityPanel from "../components/activity/InvoiceActivityPanel";
import InvoiceV2OptionsPanel from "../components/optionspanel/InvoiceV2OptionsPanel";
import NotesPanel from "note/list/app";
import { isPanelVisible } from "utilities/panelUtils";
import ReadonlyLinkedShowsPanelSegment from "document/linkshow/ReadonlyLinkedShowsPanelSegment";
import { InvoiceReminderApp } from "DocumentReminder/AppForInvoices";

interface Props {
  viewModel: server.viewmodel.InvoiceV2DetailFrontModel
}

const DraftPanel = (props: Props) => {

  const productsByParentQueryKey = ["productsByParent", "invoice", props.viewModel.details.documentId];

  const queryClient = useQueryClient();

  const onBookkeepingOrCurrencyChange = () => {
    queryClient.invalidateQueries(productsByParentQueryKey);
  }

  const isValid = async () => {
    const result = await validators.validateAll();
    if (result.hasErrors) {
      result.scrollToFirstError();
    }
    return !result.hasErrors;
  };

  const validators = useValidators();

  const validateForPreview = async () => {
    const result = await validators.validateAll();
    const isValid = !result.hasErrors;
    if (!isValid) {
      result.scrollToFirstError();
    }

    return isValid;
  }

  const missingFields = validators
    .validationResult
    .failedKeys
    .map(e => e.title);

  return <ValidatorContext settings={validators.validatorSettings}>
    <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <SectionPanel.Section>
          <InvoiceV2DraftSection
            invoice={props.viewModel.details}
            validate={isValid}
            brands={props.viewModel.brands}
            onBookkeepingOrCurrencyChange={onBookkeepingOrCurrencyChange}
            initialProductsByParent={props.viewModel.productsPanel.data}
            productsByParentQueryKey={productsByParentQueryKey}
            missingFields={missingFields}
            validateForPreview={validateForPreview}
          />

          <RecipientsPanel
            initialViewers={props.viewModel.viewers}
            invoice={props.viewModel.details}
          />

          {isPanelVisible(props.viewModel.linkedShowsPanel.config) && <ReadonlyLinkedShowsPanelSegment
            documentType={"invoice"}
            linkedShowsPanel={props.viewModel.linkedShowsPanel}
          />}

          <ProductsPanel
            productsByParentQueryKey={productsByParentQueryKey}
            documentType={"invoice"}
            documentId={props.viewModel.details.documentId}
            productsPanel={props.viewModel.productsPanel}
            validatorKey={RESX.Product.Products}
          />

          <NotesPanel
            mainArea={"invoice"}
            useSectionPanel
            config={props.viewModel.linkedNotePanelConfig}
          />

          <SectionPanel.Panel>
            <ChangeLifecycleButtons
              documentType="invoice"
              documentId={props.viewModel.details.documentId}
              right="review"
              rightValidate={isValid}
            />
          </SectionPanel.Panel>
        </SectionPanel.Section>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn size="medium">
        <InvoiceV2OptionsPanel
          optionsPanel={props.viewModel.optionsPanel}
          listUrl={props.viewModel.listUrl}
          invoice={props.viewModel.details}
          copyUrl={props.viewModel.copyUrl}
        />

        <InvoiceReminderApp
          document={props.viewModel.details}
          initialReminders={props.viewModel.reminders}
        />

        <InvoiceActivityPanel documentId={props.viewModel.details.documentId} config={props.viewModel.activityPanelConfig} />
      </TwoColumnLayout.RightColumn>
    </TwoColumnLayout>
  </ValidatorContext>
}

export default DraftPanel;
